/**
 * Lightweight analytics client plugin.
 *
 * Reads runtimeConfig.public.analyticsProvider and lazy-loads the right
 * script (or no-ops if blank). Exposes the active provider on the Nuxt
 * app context so useAnalytics() can dispatch custom events to whichever
 * provider is wired in.
 *
 * Why a single-purpose plugin instead of one-per-provider:
 *   - Keeps the CSP allowlist + env config in one place
 *   - Lets the agency owner swap Plausible → Fathom without touching code
 *   - Means dev environments with no analytics env vars are completely
 *     silent — no console noise, no failed network requests
 *
 * Tracked pageviews:
 *   - Plausible / Fathom / Umami all SPA-aware out of the box for hash-
 *     style navigation, but Nuxt uses pushState — we manually fire a
 *     pageview on every router afterEach.
 *   - GA4 (gtag) auto-tracks via the route event.
 *
 * NOTE: Honour the localStorage `analytics-opted-out=1` flag set by users
 * who explicitly opt out via the footer link. The plugin returns early if
 * the flag is present.
 */
export type AnalyticsProvider = '' | 'plausible' | 'fathom' | 'ga4' | 'umami'

interface Window {
  plausible?: (event: string, opts?: { props?: Record<string, string | number | boolean> }) => void
  fathom?: { trackEvent: (name: string, opts?: Record<string, unknown>) => void; trackPageview: () => void }
  gtag?: (...args: unknown[]) => void
  umami?: { track: (name: string, data?: Record<string, unknown>) => void }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const provider = ((config.public as any).analyticsProvider || '') as AnalyticsProvider

  // Respect explicit opt-out, however the user set it.
  if (import.meta.client && localStorage.getItem('analytics-opted-out') === '1') {
    return { provide: { analyticsProvider: '' as AnalyticsProvider } }
  }
  if (!provider) {
    return { provide: { analyticsProvider: '' as AnalyticsProvider } }
  }

  if (import.meta.server) {
    return { provide: { analyticsProvider: provider } }
  }

  // ── Inject the right loader script ───────────────────────────────
  if (provider === 'plausible' && (config.public as any).plausibleDomain) {
    const s = document.createElement('script')
    s.defer = true
    s.setAttribute('data-domain', (config.public as any).plausibleDomain)
    // Outbound-links extension gives us free "blog → social share" tracking.
    s.src = 'https://plausible.io/js/script.outbound-links.js'
    document.head.appendChild(s)

    // Plausible auto-tracks SPA navigations but the queue + script.js
    // shape both work — define the function ahead of time so calls
    // queued before the script lands still get sent.
    ;(window as any).plausible = (window as any).plausible || function () {
      ;((window as any).plausible.q = (window as any).plausible.q || []).push(arguments)
    }
  } else if (provider === 'fathom' && (config.public as any).fathomSiteId) {
    const s = document.createElement('script')
    s.defer = true
    s.setAttribute('data-site', (config.public as any).fathomSiteId)
    s.setAttribute('data-spa', 'auto')
    s.src = 'https://cdn.usefathom.com/script.js'
    document.head.appendChild(s)
  } else if (provider === 'ga4' && (config.public as any).ga4MeasurementId) {
    const id = (config.public as any).ga4MeasurementId
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
    document.head.appendChild(s)

    const inline = document.createElement('script')
    inline.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}', { send_page_view: true });`
    document.head.appendChild(inline)
  } else if (provider === 'umami' && (config.public as any).umamiUrl && (config.public as any).umamiWebsiteId) {
    const s = document.createElement('script')
    s.defer = true
    s.setAttribute('data-website-id', (config.public as any).umamiWebsiteId)
    s.src = (config.public as any).umamiUrl.replace(/\/+$/, '') + '/script.js'
    document.head.appendChild(s)
  }

  // ── SPA-aware pageview pings ─────────────────────────────────────
  // GA4 with `send_page_view: true` in config already handles SPA pageviews
  // via Nuxt's history events; the others need a manual nudge.
  const router = nuxtApp.$router as { afterEach: (cb: (to: any) => void) => void }
  router?.afterEach((to: any) => {
    // Defer to next tick so the page title is up to date.
    setTimeout(() => {
      const w = window as unknown as Window
      if (provider === 'plausible' && w.plausible) {
        w.plausible('pageview')
      } else if (provider === 'fathom' && w.fathom) {
        w.fathom.trackPageview()
      } else if (provider === 'umami' && w.umami) {
        // Umami v2 auto-tracks via History API; left here for clarity.
      } else if (provider === 'ga4' && w.gtag) {
        w.gtag('event', 'page_view', {
          page_path: to.fullPath,
          page_title: document.title,
        })
      }
    }, 0)
  })

  return { provide: { analyticsProvider: provider } }
})
